export type Box = { left: number; top: number; right: number; bottom: number }
export type Shift = { dx: number; dy: number }
export type Obstacle = { box: Box; weight: number }

const GAP = 8

const moved = (box: Box, { dx, dy }: Shift): Box => ({
  left: box.left + dx,
  right: box.right + dx,
  top: box.top + dy,
  bottom: box.bottom + dy,
})

const overlapArea = (a: Box, b: Box) =>
  Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left)) *
  Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top))

const inside = (box: Box, bounds: Box) =>
  box.left >= bounds.left &&
  box.right <= bounds.right &&
  box.top >= bounds.top &&
  box.bottom <= bounds.bottom

const clampShift = (low: number, high: number, min: number, max: number) =>
  Math.max(0, min - low) - Math.max(0, high - max)

const sidesteps = (box: Box, base: Shift, obstacle: Box): Shift[] => [
  { dx: base.dx, dy: base.dy + obstacle.top - GAP - box.bottom },
  { dx: base.dx, dy: base.dy + obstacle.bottom + GAP - box.top },
  { dx: base.dx + obstacle.left - GAP - box.right, dy: base.dy },
  { dx: base.dx + obstacle.right + GAP - box.left, dy: base.dy },
]

export const popupShift = (
  popup: Box,
  bounds: Box,
  obstacles: Obstacle[],
): Shift => {
  const edge = {
    dx: clampShift(popup.left, popup.right, bounds.left, bounds.right),
    dy: clampShift(popup.top, popup.bottom, bounds.top, bounds.bottom),
  }
  const blockers = (shift: Shift) =>
    obstacles.filter(({ box }) => overlapArea(moved(popup, shift), box) > 0)
  const stepsFrom = (shift: Shift) =>
    blockers(shift).flatMap(({ box }) =>
      sidesteps(moved(popup, shift), shift, box),
    )

  const firstStep = stepsFrom(edge)
  const secondStep = firstStep.flatMap(stepsFrom)

  const covered = (shift: Shift) =>
    obstacles.reduce(
      (sum, { box, weight }) =>
        sum + weight * overlapArea(moved(popup, shift), box),
      0,
    )
  const distance = ({ dx, dy }: Shift) => Math.abs(dx) + Math.abs(dy)

  return (
    [edge, ...firstStep, ...secondStep]
      .filter((shift) => inside(moved(popup, shift), bounds))
      .map((shift) => ({ shift, covered: covered(shift) }))
      .sort(
        (a, b) =>
          a.covered - b.covered || distance(a.shift) - distance(b.shift),
      )[0]?.shift ?? edge
  )
}
